import '@/src/api/client';
import { setBearerToken } from '@/src/api/auth-client';
import { normalizeApiError } from '@/src/api/error-utils';
import type { MembershipSummaryDto } from '@/src/api/generated';
import {
  authControllerLogout,
  authControllerLogoutAll,
  authControllerIssueLogoutAllConfirmation,
  authControllerRefresh,
  tenantsControllerSelectTenant,
} from '@/src/api/generated/sdk.gen';
import {
  clearAuthStorage,
  getAuthGeneration,
  getActiveTenantId,
  getBearerToken,
  isAuthGenerationCurrent,
  setActiveTenant,
  setIdentityToken,
  setTenantToken,
} from './token-storage';
import { publishAuthTerminalEvent } from './auth-lifecycle';
import { withAuthLock } from './web-locks';

export const AUTH_REFRESH_RACE_CODE = 'AUTH_REFRESH_RACE';
const AUTH_RACE_RETRY_DELAY_MS = 750;

/**
 * Typed refresh outcome. Only `invalid-session` represents a definitive
 * logout-worthy state. Network, 429, and 5xx failures are transient and must
 * preserve session metadata; `tenant-unavailable` means the identity session is
 * valid but no tenant could be established.
 */
export type SessionRefreshResult =
  | { status: 'refreshed' }
  | { status: 'invalid-session' }
  | { status: 'temporarily-unavailable' }
  | { status: 'throttled' }
  | { status: 'tenant-unavailable' };

export type SessionLogoutResult =
  | { status: 'logged-out' }
  | { status: 'failed' };

type SdkResponse<T> = {
  data?: T;
  error?: unknown;
  response?: Response;
};

let refreshRequest: {
  generation: number;
  promise: Promise<SessionRefreshResult>;
} | null = null;

function selectPreferredMembership(memberships: MembershipSummaryDto[]) {
  const activeTenantId = getActiveTenantId();

  return memberships.find((membership) => membership.tenant.id === activeTenantId) ?? memberships[0] ?? null;
}

function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isAuthRefreshRace(response: SdkResponse<unknown>): boolean {
  return (
    response.response?.status === 409 &&
    normalizeApiError(response.error).code === AUTH_REFRESH_RACE_CODE
  );
}

function classifyRefreshFailure(response: SdkResponse<unknown>): SessionRefreshResult['status'] {
  const status = response.response?.status ?? null;

  if (status === 401) return 'invalid-session';
  if (status === 429) return 'throttled';
  // No response (network failure) or any other status is treated as transient
  // so a valid session is never cleared on a recoverable error.
  return 'temporarily-unavailable';
}

function classifyTenantFailure(response: SdkResponse<unknown>): SessionRefreshResult['status'] {
  const status = response.response?.status ?? null;

  if (status === 401) return 'invalid-session';
  if (status === 403) return 'tenant-unavailable';
  if (status === null || (status >= 500 && status < 600)) {
    return 'temporarily-unavailable';
  }
  // Any other tenant-selection problem leaves the identity session valid.
  return 'tenant-unavailable';
}

type RefreshData = { accessToken: string; memberships: MembershipSummaryDto[] };

async function attemptRefresh(
  generation: number,
): Promise<{ refreshed: true; data: RefreshData } | { refreshed: false; result: SessionRefreshResult }> {
  const callRefresh = () => authControllerRefresh({ credentials: 'include' });

  const response = await callRefresh();

  if (!isAuthGenerationCurrent(generation)) {
    return { refreshed: false, result: { status: 'temporarily-unavailable' } };
  }

  const accessToken = response.data?.accessToken;
  if (response.error || !accessToken) {
    if (isAuthRefreshRace(response)) {
      // A concurrent tab (or a client without Web Locks) rotated the cookie.
      // Wait briefly for the updated cookie to land, then retry exactly once.
      await delay(AUTH_RACE_RETRY_DELAY_MS);
      if (!isAuthGenerationCurrent(generation)) {
        return { refreshed: false, result: { status: 'temporarily-unavailable' } };
      }
      const retry = await callRefresh();
      const retryAccessToken = retry.data?.accessToken;
      if (!isAuthGenerationCurrent(generation) || retry.error || !retryAccessToken) {
        return { refreshed: false, result: { status: classifyRefreshFailure(retry) } };
      }
      return {
        refreshed: true,
        data: {
          accessToken: retryAccessToken,
          memberships: retry.data?.memberships ?? [],
        },
      };
    }
    return { refreshed: false, result: { status: classifyRefreshFailure(response) } };
  }

  return {
    refreshed: true,
    data: {
      accessToken,
      memberships: response.data?.memberships ?? [],
    },
  };
}

async function establishTenantSession(
  generation: number,
  data: { accessToken: string; memberships: MembershipSummaryDto[] },
): Promise<SessionRefreshResult> {
  // Preserve the valid identity session before tenant selection so a transient
  // tenant failure does not discard an otherwise-good refresh.
  if (!setIdentityToken(data.accessToken, generation)) {
    return { status: 'temporarily-unavailable' };
  }

  const selectedMembership = selectPreferredMembership(data.memberships);

  if (!selectedMembership) {
    return { status: 'tenant-unavailable' };
  }

  const tenantResponse = await tenantsControllerSelectTenant({
    auth: data.accessToken,
    body: { tenantId: selectedMembership.tenant.id },
  });

  if (!isAuthGenerationCurrent(generation)) {
    return { status: 'temporarily-unavailable' };
  }

  if (tenantResponse.error || !tenantResponse.data?.accessToken) {
    return { status: classifyTenantFailure(tenantResponse) };
  }

  if (!setTenantToken(tenantResponse.data.accessToken, generation)) {
    return { status: 'temporarily-unavailable' };
  }

  setActiveTenant(tenantResponse.data.tenant, generation);
  setBearerToken(tenantResponse.data.accessToken);

  return { status: 'refreshed' };
}

async function refreshSession(generation: number): Promise<SessionRefreshResult> {
  return withAuthLock(async () => {
    try {
      const outcome = await attemptRefresh(generation);
      if (!outcome.refreshed) {
        return outcome.result;
      }
      return establishTenantSession(generation, outcome.data);
    } catch {
      return { status: 'temporarily-unavailable' } as const;
    }
  });
}

export function refreshStoredTenantSession(): Promise<SessionRefreshResult> {
  const generation = getAuthGeneration();
  if (refreshRequest?.generation === generation) return refreshRequest.promise;
  const request = {
    generation,
    promise: Promise.resolve({ status: 'temporarily-unavailable' } as SessionRefreshResult),
  };
  request.promise = refreshSession(generation).finally(() => {
    if (refreshRequest === request) refreshRequest = null;
  });
  refreshRequest = request;
  return request.promise;
}

export async function logoutCurrentSession(): Promise<SessionLogoutResult> {
  // Local authority is invalidated before the request so refresh/select
  // responses already in flight can never revive the signed-out session.
  clearAuthStorage();
  setBearerToken(null);
  return withAuthLock(async () => {
    try {
      const response = await authControllerLogout({ credentials: 'include' });

      if (response.error || !response.data?.success) {
        return { status: 'failed' };
      }

      // Publish only after revocation is authoritative so receivers cannot race
      // the server and incorrectly validate against a not-yet-revoked cookie.
      publishAuthTerminalEvent('explicit_logout');
      return { status: 'logged-out' };
    } catch {
      return { status: 'failed' };
    }
  });
}

export type LogoutAllConfirmationResult =
  | { status: 'confirmed'; nonce: string; clientActionId: string }
  | { status: 'failed' };

/**
 * Requests a short-lived, single-use confirmation nonce from the backend for
 * the destructive logout-all action (#491). The nonce must be passed to
 * {@link logoutAllSessions} within its validity window.
 *
 * This is the ONLY function that should call the confirmation endpoint. No
 * mount, effect, background sync, or auth-recovery path should invoke it.
 */
export async function requestLogoutAllConfirmation(): Promise<LogoutAllConfirmationResult> {
  const bearerToken = getBearerToken();
  if (!bearerToken) return { status: 'failed' };

  try {
    const response = await authControllerIssueLogoutAllConfirmation({
      auth: bearerToken,
      credentials: 'include',
      body: { sourceCategory: 'current_ui' },
    });

    if (response.error || !response.data?.nonce) {
      return { status: 'failed' };
    }

    return {
      status: 'confirmed',
      nonce: response.data.nonce,
      clientActionId: response.data.clientActionId,
    };
  } catch {
    return { status: 'failed' };
  }
}

export async function logoutAllSessions(
  confirmationNonce: string,
): Promise<SessionLogoutResult> {
  // logout-all requires the current bearer, so snapshot it before invalidating
  // local authority. Late refresh/select responses are still rejected by the
  // generation change even when the revocation request fails.
  const bearerToken = getBearerToken();
  clearAuthStorage();
  setBearerToken(null);

  if (!bearerToken) return { status: 'failed' };

  return withAuthLock(async () => {
    try {
      const response = await authControllerLogoutAll({
        auth: bearerToken,
        credentials: 'include',
        body: { confirmationNonce },
      });

      if (response.error || !response.data?.success) {
        return { status: 'failed' };
      }

      publishAuthTerminalEvent('logout_all');
      return { status: 'logged-out' };
    } catch {
      return { status: 'failed' };
    }
  });
}
